import React, { useState } from 'react';
import { Button } from '@/components/common';
import { describeInvitationError } from '@/hooks/useInvitations';
import { useTransientNotice } from '@/hooks/useTransientNotice';
import { RoomRole, type InvitationSummary } from '@shared/types.js';
import { countOf } from '@/utils/plural';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInbox,
  FaRedo,
  FaSpinner,
  FaTrash
} from 'react-icons/fa';

const ROLE_LABELS: Record<string, string> = {
  [RoomRole.ADMIN]: 'Admin',
  [RoomRole.PLAYER]: 'Resident',
  [RoomRole.GUEST]: 'Guest'
};

interface PendingInvitationsProps {
  invitations: InvitationSummary[];
  isLoading: boolean;
  error: string | null;
  onRevoke: (invitationId: string) => Promise<void>;
  onResend: (invitationId: string) => Promise<void>;
}

/**
 * The island's live invitations. Resend mints a new token and resets the expiry
 * — which also kills the previous link — and Revoke kills it outright.
 */
export const PendingInvitations: React.FC<PendingInvitationsProps> = ({
  invitations,
  isLoading,
  error,
  onRevoke,
  onResend
}) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // A resend answers with nothing the row shows; without a word the button
  // reads as broken, exactly as sending one without its notice would.
  const resent = useTransientNotice();

  const run = async (
    id: string,
    action: () => Promise<void>,
    fallback: string,
    onDone?: () => void
  ) => {
    setBusyId(id);
    setActionError(null);
    resent.clear();
    try {
      await action();
      onDone?.();
    } catch (err) {
      setActionError(describeInvitationError(err, fallback));
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading && invitations.length === 0) {
    return (
      <div className="flex items-center gap-2 text-gray-300 text-sm py-4">
        <FaSpinner className="animate-spin text-game-golden" />
        <span>Loading invitations…</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(error || actionError) && (
        <div className="p-3 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg text-sm flex items-center gap-2">
          <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
          <span>{actionError ?? error}</span>
        </div>
      )}

      {resent.notice && (
        <div className="p-3 bg-game-medium/20 border border-game-medium/50 text-green-300 rounded-lg text-sm flex items-center gap-2">
          <FaCheckCircle className="flex-shrink-0" />
          <span>{resent.notice}</span>
        </div>
      )}

      {invitations.length === 0 ? (
        <div className="flex items-center gap-3 text-gray-300 text-sm py-4">
          <FaInbox className="text-game-light" />
          <span>No invitations are waiting to be accepted.</span>
        </div>
      ) : (
        <ul className="space-y-2 list-none p-0 m-0">
          {invitations.map((invitation) => (
            <li
              key={invitation.id}
              className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-game-dark/30 border border-game-medium/20"
            >
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{invitation.email}</p>
                <p className="text-xs text-gray-400">
                  {invitation.role ? ROLE_LABELS[invitation.role] ?? invitation.role : 'Platform'}
                  {invitation.guestDays
                    ? ` · ${countOf(invitation.guestDays, 'day')}`
                    : ''}
                  {' · expires '}
                  {new Date(invitation.expiresAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  className="px-3 py-1.5 text-sm"
                  isLoading={busyId === invitation.id}
                  onClick={() =>
                    run(
                      invitation.id,
                      () => onResend(invitation.id),
                      'Failed to resend the invitation',
                      () => resent.show(`Invitation re-sent to ${invitation.email}`)
                    )
                  }
                >
                  <FaRedo className="text-xs" />
                  <span>Resend</span>
                </Button>
                <Button
                  variant="danger"
                  type="button"
                  className="px-3 py-1.5 text-sm"
                  disabled={busyId === invitation.id}
                  onClick={() =>
                    run(invitation.id, () => onRevoke(invitation.id), 'Failed to revoke the invitation')
                  }
                >
                  <FaTrash className="text-xs" />
                  <span>Revoke</span>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
